import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { USER_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/user/user-registry-select.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { BaseMultiSelectComponent } from '@zaoblako/web/shared/components/base-multi-select/base-multi-select.component';
import type { DropdownQueryInterface } from '@zaoblako/web/shared/components/base-multi-select/base-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { isStrictHydrationRequest } from '@zaoblako/web/shared/helpers/include-ids-pagination.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { map, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-user-multi-select',
  templateUrl: './user-multi-select.component.html',
  imports: [CommonModule, MultiSelectComponent, SpinnerComponent, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserMultiSelectComponent extends BaseMultiSelectComponent<UserFindInterface> implements OnInit, OnChanges {
  public availableModule = 'BASIS';
  protected readonly entityCacheKind = 'user';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';
  @Input() public withBots = false;
  @Input() public customRequest = false;
  @Input() public label = 'Пользователь';
  @Input() public scrollHeight: string = '300px';
  @Input() public itemSubDescriptionField = 'job';
  @Input() public display = 'chip';
  @Input() public moveDown = false;
  @Input() public sort = 'profile.name:string';

  @Input() public users: UserFindInterface[] = [];
  @Output() public selectedUsersChange = new EventEmitter<UserFindInterface[]>();

  @Input() public set selectedUsers(value: UserFindInterface[]) {
    this.selectedEntities = value;
  }

  @Input() public set setSelectedUserIds(ids: number[]) {
    this.setSelectedEntityIds = ids;
  }

  public constructor(private readonly userService: UserService) {
    super();
  }

  public override ngOnInit(): void {
    this.customSearch = this.customRequest && !!this.users?.length;
    if (this.customSearch && this.users?.length) {
      this.entities = this.users;
    }
    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.customRequest || changes.users) {
      this.customSearch = this.customRequest && !!this.users?.length;
      if (this.customSearch && this.users?.length) {
        this.entities = this.users ?? [];
        this.cdRef.markForCheck();
      } else if (!this.customSearch && !changes.customRequest?.firstChange) {
        this.firstLoad();
      }
    }
    if (changes.users && this.customSearch) {
      this.entities = this.users ?? [];
      this.cdRef.markForCheck();
    }
    if (changes.withBots && !changes.withBots.firstChange && !this.customSearch) {
      this.firstLoad();
    }
    super.ngOnChanges(changes);
  }

  public findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<UserFindInterface>> {
    return this.userService.findRegistryItems({
      ...queryParams,
      limit: queryParams?.limit ?? DEFAULT_LIMIT,
      offset: queryParams?.offset ?? DEFAULT_OFFSET,
      select: [...USER_WIDGET_REGISTRY_SELECT],
      withBots: this.withBots
    }).pipe(
      map(({ items, count, limit, offset }) => ({ items, count, limit, offset }))
    );
  }

  protected override getRegistryCacheContext(): Record<string, unknown> {
    return { withBots: this.withBots };
  }

  protected override augmentHydrationQuery(
    query: Partial<DropdownQueryInterface<UserFindInterface['id']>>
  ): Partial<DropdownQueryInterface<UserFindInterface['id']>> {
    if (!isStrictHydrationRequest(query)) {
      return query;
    }

    return {
      ...query,
      withBanned: true,
      withDeleted: true
    } as Partial<DropdownQueryInterface<UserFindInterface['id']>>;
  }

  public onSelect(users: UserFindInterface[]): void {
    if (this.shouldIgnoreSelectionChange(users)) {
      return;
    }

    this.selectedUsersChange.emit(users);
    super.onSelect(users);
  }

  public toUser(user: unknown): UserFindInterface {
    return user as UserFindInterface;
  }
}
